import {
    isArray
} from 'lodash-es'

import {
    axios
} from '@util'

class CashCallService {

    // LIQ-2609: fresh SAS URL for a cash call download link, for a user who
    // was sent to login by that link. Goes through the JWT API because the
    // SPA login holds no Django session, so a plain navigation to the public
    // download link would not see the user as logged in.
    async getDownloadUrl(token) {
        return await axios({
            url: `/cashcall/download/${token}/url/`,
            method: 'GET',
            skipToast: true
        })
    }

    async list(role, role_value, user, userId, params = {}, options = {}) {
        const query = Object.entries(params)
            .filter(([_, value]) => !!value)
            .map(([key, value]) => {
                if (!isArray(value)) return `${key}=${encodeURIComponent(value)}`

                return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&')
            })
            .join('&')

        return await axios({
            url: `cashcall/list/?${query}`,
            params: {
                role,
                role_value,
                user,
                userId
            }
        })
    }

    async getBdxKeys(payload = {}) {
        return await axios({
            url: '/cashcall/get/bdxkeys/',
            method: 'POST',
            data: payload
        })
    }

    async getClaims(data = {}) {
        let url = '/cashcall/get/claims/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    async getClaimDetails(data = {}) {
        let url = '/cashcall/get/claimsData/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    async getTPA(data = {}) {
        let url = '/cashcall/get/tpa/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    async getUnderwriter(data = {}) {
        let url = '/cashcall/get/underwriter/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    // Full distinct list of LEAD underwriter names in the system (not scoped to
    // a key). Used by the Co-Lead "Add missing UMR" Lead Underwriter dropdown.
    async getLeadUnderwriters() {
        return await axios({
            url: '/cashcall/get/lead-underwriters/',
            method: 'GET'
        });
    }

    async getCoverholder(data = {}) {
        let url = '/cashcall/get/coverholder/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    async getBroker(data = {}) {
        let url = '/cashcall/get/broker/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    async setBroker(data = {}) {
        let url = '/cashcall/post/rls-broker/';
        return await axios({
            url: url,
            method: 'POST',
            data: data
        });
    }

    async createCashCall(payload = {}) {
        return await axios({
            url: '/cashcall/create/',
            method: 'POST',
            data: payload
        })
    }

    async getGlobalUMRClaimsBulk() {
        return await axios({
            url: '/cashcall/get/global-umr-claims-bulk/',
            method: 'POST'
        })
    }

    async createCashCallGroup(payload = {}) {
        return await axios({
            url: '/cashcall/group/create/',
            method: 'POST',
            data: payload
        })
    }

    async uploadFile(cash_call_id, formData) {
        return await axios({
            url: `/cashcall/document/upload/${cash_call_id}`,
            method: 'POST',
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            data: formData
        })
    }

    async deleteFile(cash_call_id, ETAG, doc_type) {
        return await axios({
            url: `/cashcall/document/delete/${ETAG}/${cash_call_id}?doc_type=${doc_type}`,
            method: 'DELETE'
        })
    }

    async getRecipients(cash_call_id) {
        return await axios({
            url: `/cashcall/get/recipients/?cash_call_id=${cash_call_id}`,
            method: 'GET'
        })
    }

    async getSavedRecipients(cash_call_id) {
        return await axios({
            url: `/cashcall/get/saved-recipients/?cash_call_id=${cash_call_id}`,
            method: 'GET'
        })
    }

    async getAllowedEmailDomains(cash_call_id) {
        return await axios({
            url: `/cashcall/get/allowed-email-domains/?cash_call_id=${cash_call_id}`,
            method: 'GET'
        })
    }

    async sendEmail(formData) {
        return await axios({
            url: `/cashcall/send-email/`,
            method: 'POST',
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            data: formData
        })
    }

    async deleteCashCall(id) {
        return await axios({
            url: `/cashcall/delete/${id}`,
            method: 'DELETE'
        })
    }

    async updateStatus(id, status) {
        return await axios({
            url: `/cashcall/update-status/${id}`,
            method: 'PATCH',
            data: {
                status: status
            }
        })
    }

    // Record a single partial payment (amount + date) against one Co-Lead key.
    // The backend flips the whole cash call to PAID only once every key is
    // fully covered; otherwise it stays SUBMITTED.
    async addKeyPayment(keyId, payload = {}) {
        return await axios({
            url: `/cashcall/key/${keyId}/payment/`,
            method: 'POST',
            data: payload,
            // The caller shows validation errors (e.g. "Paid Date cannot be
            // earlier than Submitted Date.") inline under the key, so suppress
            // the global interceptor's generic "Bad Request" toast for this call.
            skipToast: true
        })
    }

    // Reject ONE co-lead key (UMR/lead underwriter). Only that key is rejected;
    // the others stay active.
    async rejectKey(keyId, payload = {}) {
        return await axios({
            url: `/cashcall/key/${keyId}/reject/`,
            method: 'POST',
            data: payload,
            skipToast: true
        })
    }

    // Canonical Risk Code list for the Add Missing UMR dropdown.
    async getRiskCodes() {
        return await axios({
            url: '/cashcall/risk-codes/',
            method: 'GET'
        })
    }

}

export default new CashCallService()